/**
 * Auth Zod Schemas
 *
 * Single source of truth for auth-related types. These Zod schemas generate:
 * 1. TypeScript types (via z.infer)
 * 2. OpenAPI schemas (via @asteasolutions/zod-to-openapi)
 *
 * @see lib/auth/types.ts - Uses types derived from these schemas
 * @see lib/swagger/registry.ts - Registers these schemas for OpenAPI generation
 */

import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';

import { RECAPTCHA_SITE_KEY } from '@/lib/const';
import { passwordSchema } from '@/lib/validations/password';

// Extend Zod with OpenAPI methods
extendZodWithOpenApi(z);

// =============================================================================
// Token Schemas
// =============================================================================

/**
 * Tokens returned from Saleor tokenCreate/tokenRefresh
 */
export const AuthTokensSchema = z
  .object({
    accessToken: z.string().openapi({
      description: 'Access token (JWT) for API requests',
    }),
    refreshToken: z.string().openapi({
      description: 'Refresh token for obtaining new access tokens',
    }),
    csrfToken: z.string().optional().openapi({
      description: 'CSRF token from Saleor',
    }),
  })
  .openapi('AuthTokens');

/**
 * Encrypted token payload stored in cookie
 */
export const EncryptedTokenPayloadSchema = z
  .object({
    accessToken: z.string().openapi({
      description: 'Access token (JWT)',
    }),
    refreshToken: z.string().openapi({
      description: 'Refresh token for obtaining new access tokens',
    }),
  })
  .openapi('EncryptedTokenPayload');

// =============================================================================
// User Metadata Schemas
// =============================================================================

/**
 * User metadata key-value pair from Saleor
 */
export const UserMetadataSchema = z
  .object({
    key: z.string().openapi({
      description: 'Metadata key',
      example: 'salesforceId',
    }),
    value: z.string().openapi({
      description: 'Metadata value',
      example: 'SF12345',
    }),
  })
  .openapi('UserMetadata');

/**
 * Country information
 */
export const CountrySchema = z
  .object({
    code: z.string().openapi({
      description: 'ISO 3166-1 alpha-2 country code',
      example: 'US',
    }),
    country: z.string().openapi({
      description: 'Country name',
      example: 'United States',
    }),
  })
  .openapi('Country');

/**
 * User address from Saleor
 */
export const UserAddressSchema = z
  .object({
    id: z.string().openapi({
      description: 'Address ID',
    }),
    firstName: z.string().openapi({
      description: 'First name',
    }),
    lastName: z.string().openapi({
      description: 'Last name',
    }),
    companyName: z.string().optional().openapi({
      description: 'Company name',
    }),
    streetAddress1: z.string().openapi({
      description: 'Street address line 1',
    }),
    streetAddress2: z.string().optional().openapi({
      description: 'Street address line 2',
    }),
    city: z.string().openapi({
      description: 'City',
    }),
    cityArea: z.string().optional().openapi({
      description: 'City area/district',
    }),
    postalCode: z.string().openapi({
      description: 'Postal/ZIP code',
    }),
    countryArea: z.string().optional().openapi({
      description: 'State/province/region',
    }),
    phone: z.string().optional().openapi({
      description: 'Phone number',
    }),
    country: CountrySchema.openapi({
      description: 'Country information',
    }),
    isDefaultBillingAddress: z.boolean().optional().openapi({
      description: 'Whether this is the default billing address',
    }),
    isDefaultShippingAddress: z.boolean().optional().openapi({
      description: 'Whether this is the default shipping address',
    }),
    metadata: z.array(UserMetadataSchema).optional().openapi({
      description: 'Address metadata (e.g. personal title)',
    }),
  })
  .openapi('UserAddress');

// =============================================================================
// User Schemas
// =============================================================================

/**
 * Authenticated user data from Saleor
 */
export const AuthUserSchema = z
  .object({
    id: z.string().openapi({
      description: 'User ID',
    }),
    email: z.string().email().openapi({
      description: 'User email address',
    }),
    firstName: z.string().openapi({
      description: 'First name',
    }),
    lastName: z.string().openapi({
      description: 'Last name',
    }),
    metadata: z.array(UserMetadataSchema).openapi({
      description: 'User metadata key-value pairs',
    }),
    addresses: z.array(UserAddressSchema).openapi({
      description: 'User addresses',
    }),
    defaultBillingAddress: UserAddressSchema.nullable().openapi({
      description: 'Default billing address',
    }),
    defaultShippingAddress: UserAddressSchema.nullable().openapi({
      description: 'Default shipping address',
    }),
  })
  .openapi('AuthUser');

/**
 * Slim user data for cookie storage (must be under 4KB)
 */
export const CookieUserSchema = z
  .object({
    id: z.string().openapi({
      description: 'User ID',
    }),
    email: z.string().email().openapi({
      description: 'User email address',
    }),
    firstName: z.string().openapi({
      description: 'First name',
    }),
    lastName: z.string().openapi({
      description: 'Last name',
    }),
    metadata: z.array(UserMetadataSchema).openapi({
      description: 'Essential metadata only (e.g., salesforce ID)',
    }),
  })
  .openapi('CookieUser');

// =============================================================================
// Auth Error Schema
// =============================================================================

/**
 * Auth error from Saleor
 * Note: This is identical to ApiError but kept separate for domain clarity
 */
export const AuthErrorSchema = z
  .object({
    field: z.string().nullable().openapi({
      description: 'Field that caused the error, if applicable',
    }),
    message: z.string().nullable().openapi({
      description: 'Human-readable error message',
    }),
    code: z.string().openapi({
      description: 'Error code for programmatic handling',
    }),
  })
  .openapi('AuthError');

// =============================================================================
// Request/Response Schemas
// =============================================================================

/**
 * Login request payload
 */
export const LoginRequestSchema = z
  .object({
    email: z.string().email().openapi({
      description: 'User email address',
      example: 'user@example.com',
    }),
    password: z.string().min(1).openapi({
      description: 'User password',
      example: 'password123',
    }),
  })
  .openapi('LoginRequest');

/**
 * Login response data (success case)
 */
export const LoginResponseDataSchema = z
  .object({
    user: AuthUserSchema.openapi({
      description: 'Authenticated user data',
    }),
  })
  .openapi('LoginResponseData');

/**
 * Login response from BFF
 */
export const LoginResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the login was successful',
    }),
    data: LoginResponseDataSchema.optional().openapi({
      description: 'User data on successful login',
    }),
    errors: z.array(AuthErrorSchema).optional().openapi({
      description: 'Errors on failed login',
    }),
  })
  .openapi('LoginResponse');

/**
 * Me response from BFF
 */
export const MeResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the request was successful',
    }),
    user: AuthUserSchema.nullable().openapi({
      description: 'Current user data, or null if not authenticated',
    }),
    errors: z.array(AuthErrorSchema).optional().openapi({
      description: 'Errors if request failed',
    }),
  })
  .openapi('MeResponse');

/**
 * Refresh response from BFF
 */
export const RefreshResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the token refresh was successful',
    }),
    errors: z.array(AuthErrorSchema).optional().openapi({
      description: 'Errors if refresh failed',
    }),
  })
  .openapi('RefreshResponse');

/**
 * Logout response from BFF
 */
export const LogoutResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the logout was successful',
    }),
  })
  .openapi('LogoutResponse');

// =============================================================================
// Registration Schemas
// =============================================================================

/**
 * Metadata keys a registrant is allowed to set.
 * Privileged keys (salesforce_id, is_club_member, accountSource, etc.)
 * are deliberately excluded — the server forces those values.
 *
 * The consent flags (newsletterConsent, btqOptIn, is_affiliated) are always
 * sent by the register form and are required by the Salesforce sync flow.
 * JP name keys (kanji/katakana/romaji) are optional and forwarded to CPI/Salesforce.
 * Must stay in sync with every key japaneseNameMetadata() can emit — an unlisted
 * key fails the whole request, not just that entry.
 */
const ALLOWED_REGISTRATION_METADATA_KEYS = [
  'user.personalTitle',
  'user.phone',
  'user.phonePrefix',
  'user.dob',
  'user.newsletterConsent',
  'user.btqOptIn',
  'user.is_affiliated',
  'user.first_name_kanji',
  'user.last_name_kanji',
  'user.first_name_katakana',
  'user.last_name_katakana',
  'user.first_name_romaji',
  'user.last_name_romaji',
] as const;

const RegistrationMetadataEntrySchema = z.object({
  key: z.enum(ALLOWED_REGISTRATION_METADATA_KEYS).openapi({
    description: 'Whitelisted metadata key',
  }),
  value: z.string().max(200).openapi({
    description: 'Metadata value',
  }),
});

/**
 * Register request payload
 */
export const RegisterRequestSchema = z
  .object({
    email: z.string().email().openapi({
      description: 'User email address',
      example: 'user@example.com',
    }),
    password: passwordSchema,
    firstName: z.string().min(1).openapi({
      description: 'First name',
      example: 'Jane',
    }),
    lastName: z.string().min(1).openapi({
      description: 'Last name',
      example: 'Doe',
    }),
    locale: z.string().openapi({
      description: 'Locale string for channel derivation (e.g. us-en)',
      example: 'us-en',
    }),
    metadata: z.array(RegistrationMetadataEntrySchema).optional().openapi({
      description: 'Optional user metadata (only whitelisted keys accepted)',
    }),
    recaptchaToken: z.string().min(1).openapi({
      description: 'Google reCAPTCHA v2 token from the registration form',
    }),
  })
  .openapi('RegisterRequest');

/**
 * Register response from BFF
 */
export const RegisterResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the registration was successful',
    }),
    requiresConfirmation: z.boolean().optional().openapi({
      description: 'Whether the account requires email confirmation before use',
    }),
    data: z
      .object({
        user: AuthUserSchema.openapi({
          description: 'Registered user data',
        }),
      })
      .optional()
      .openapi({
        description: 'User data on successful registration',
      }),
    errors: z.array(AuthErrorSchema).optional().openapi({
      description: 'Errors on failed registration',
    }),
  })
  .openapi('RegisterResponse');

// =============================================================================
// Account Confirmation Schemas
// =============================================================================

/**
 * Confirm account request payload (from email confirmation link)
 */
export const ConfirmAccountRequestSchema = z
  .object({
    email: z.string().min(1).openapi({
      description: 'User email address (may be encrypted)',
    }),
    token: z.string().min(1).openapi({
      description: 'Account confirmation token from email',
    }),
  })
  .openapi('ConfirmAccountRequest');

/**
 * Confirm account response from BFF
 */
export const ConfirmAccountResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the account was confirmed successfully',
    }),
    errors: z.array(AuthErrorSchema).optional().openapi({
      description: 'Errors on failed confirmation',
    }),
  })
  .openapi('ConfirmAccountResponse');

// =============================================================================
// Password Reset Schemas
// =============================================================================

/**
 * Forgot password request payload
 */
export const ForgotPasswordRequestSchema = z
  .object({
    email: z.string().email().openapi({
      description: 'User email address',
      example: 'user@example.com',
    }),
    locale: z.string().openapi({
      description: 'Locale string for channel derivation',
      example: 'us-en',
    }),
    recaptchaToken: z.string().optional().openapi({
      description: 'Google reCAPTCHA response token',
    }),
  })
  .superRefine((data, ctx) => {
    if (RECAPTCHA_SITE_KEY && !data.recaptchaToken?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'reCAPTCHA is required',
        path: ['recaptchaToken'],
      });
    }
  })
  .openapi('ForgotPasswordRequest');

/**
 * Forgot password response from BFF (always 200 to prevent email enumeration)
 */
export const ForgotPasswordResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Always true — we do not reveal whether the email exists',
    }),
  })
  .openapi('ForgotPasswordResponse');

/**
 * Reset password request payload (set new password with token)
 */
export const ResetPasswordRequestSchema = z
  .object({
    email: z.string().email().openapi({
      description: 'User email address',
      example: 'user@example.com',
    }),
    password: passwordSchema,
    token: z.string().min(1).openapi({
      description: 'Password reset token from email',
    }),
  })
  .openapi('ResetPasswordRequest');

/**
 * Reset password response from BFF
 */
export const ResetPasswordResponseSchema = z
  .object({
    success: z.boolean().openapi({
      description: 'Whether the password was set successfully',
    }),
    authenticated: z.boolean().optional().openapi({
      description: 'Whether the user was automatically logged in after password reset',
    }),
    errors: z.array(AuthErrorSchema).optional().openapi({
      description: 'Errors on failed password reset',
    }),
  })
  .openapi('ResetPasswordResponse');

// =============================================================================
// Derived TypeScript Types
// =============================================================================

/** Tokens returned from Saleor tokenCreate/tokenRefresh */
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

/** Encrypted token payload stored in cookie */
export type EncryptedTokenPayload = z.infer<typeof EncryptedTokenPayloadSchema>;

/** User metadata key-value pair from Saleor */
export type UserMetadata = z.infer<typeof UserMetadataSchema>;

/** Country information */
export type Country = z.infer<typeof CountrySchema>;

/** User address from Saleor */
export type UserAddress = z.infer<typeof UserAddressSchema>;

/** Authenticated user data from Saleor */
export type AuthUser = z.infer<typeof AuthUserSchema>;

/** Slim user data for cookie storage */
export type CookieUser = z.infer<typeof CookieUserSchema>;

/** Auth error from Saleor */
export type AuthError = z.infer<typeof AuthErrorSchema>;

/** Login request payload */
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** Login response data */
export type LoginResponseData = z.infer<typeof LoginResponseDataSchema>;

/** Login response from BFF */
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** Me response from BFF */
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Refresh response from BFF */
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;

/** Logout response from BFF */
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

/** Register request payload */
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/** Register response from BFF */
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

/** Forgot password request payload */
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

/** Forgot password response from BFF */
export type ForgotPasswordResponse = z.infer<typeof ForgotPasswordResponseSchema>;

/** Reset password request payload */
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

/** Reset password response from BFF */
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;

/** Confirm account request payload */
export type ConfirmAccountRequest = z.infer<typeof ConfirmAccountRequestSchema>;

/** Confirm account response from BFF */
export type ConfirmAccountResponse = z.infer<typeof ConfirmAccountResponseSchema>;
